/**
 * Source & cause detection from a JS stack trace.
 * We capture `new Error().stack` at the call site, drop SDK frames, and read the
 * first application frame for file/line/function. The full stack also feeds the
 * cause heuristic (effect / user-action / polling / route-load).
 */
import type { Cause, EventSource } from '@aris/request-watcher-shared';

interface ParsedFrame {
  fn: string;
  file: string;
  line: number;
  col: number;
}

// Chrome/Node:  "at fnName (file:line:col)" | "at file:line:col"
const CHROME = /^\s*at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?\s*$/;
// Firefox/Safari: "fnName@file:line:col"
const FIREFOX = /^\s*(?:(.*?)@)?(.+?):(\d+):(\d+)\s*$/;

function parseLine(line: string): ParsedFrame | null {
  const m = CHROME.exec(line) ?? FIREFOX.exec(line);
  if (!m) return null;
  return {
    fn: (m[1] ?? '').trim() || '<anonymous>',
    file: m[2] ?? '',
    line: Number(m[3] ?? 0),
    col: Number(m[4] ?? 0),
  };
}

// Frames that belong to the SDK or runtime — skip them when picking the app frame.
const SKIP = /request-watcher|aris[-_]?watcher|node_modules\/@aris|\binterceptors\b|sdk-react/i;

function cleanFile(file: string): string {
  // Trim origin + query for readability: http://host/src/x.tsx?t=1 -> src/x.tsx
  try {
    const u = new URL(file, 'http://_');
    let p = u.pathname.replace(/^\/@fs/, '').replace(/^\/+/, '');
    return p || file;
  } catch {
    return file;
  }
}

export interface SourceResult {
  source: EventSource;
  cause: Cause;
}

export function detectSource(stack: string | undefined): SourceResult {
  const empty: EventSource = { file: null, line: null, column: null, function: null, component: null };
  if (!stack) return { source: empty, cause: 'unknown' };

  const lines = stack.split('\n');
  const frames = lines.map(parseLine).filter((f): f is ParsedFrame => !!f);

  // Cause heuristic — scan the whole stack for tell-tale frames.
  const joined = frames.map((f) => f.fn).join(' ');
  let cause: Cause = 'unknown';
  if (/useEffect|useLayoutEffect|commitHookEffect/i.test(joined)) cause = 'effect';
  else if (/onClick|onSubmit|onChange|handle[A-Z]|dispatchEvent/i.test(joined)) cause = 'user-action';
  else if (/setInterval|setTimeout|poll/i.test(joined)) cause = 'polling';

  // First non-SDK frame is our best "where".
  const appFrame = frames.find((f) => !SKIP.test(f.file) && !SKIP.test(f.fn));
  if (!appFrame) return { source: empty, cause };

  // Heuristic React component: PascalCase function name.
  const component = /^[A-Z][A-Za-z0-9]+$/.test(appFrame.fn) ? appFrame.fn : null;

  return {
    source: {
      file: cleanFile(appFrame.file),
      line: appFrame.line,
      column: appFrame.col,
      function: appFrame.fn,
      component,
    },
    cause,
  };
}

export function captureStack(): string | undefined {
  const err = new Error();
  return err.stack;
}
